import { createApi } from '@reduxjs/toolkit/query/react';
import { authRequest } from '../store/authorizedRequests';
import { ANALYSIS_API_URL } from '../settings/config';
import {
    AnalysisStructure,
    CreateAnalysisReportParams,
    CreateAnalysisReportResponse,
    CreateBackgroundAnalysisParams,
    CreateBackgroundAnalysisResponse,
    FetchAnalysisStructureParams,
    FetchDomainUrlsParams,
    FetchDomainUrlsResponse,
    FetchOverviewsParams,
    FetchSavedReportParams,
    FetchSectionResultParams,
    FetchSectionResultResponse,
    SavedAnalysisOverview,
    SavedAnalysisReport,
    SaveReportParams,
    SaveReportResponse,
} from './types';
import { getOverviewApiEndDate } from './utils';
import moment from 'moment';

const analysisBaseUrl = `${ANALYSIS_API_URL}`;
const ANALYSIS_REFETCH_TIME = 7200;

export const analysisApi = createApi({
    reducerPath: 'api.analysis',

    keepUnusedDataFor: ANALYSIS_REFETCH_TIME,

    tagTypes: ['Overviews'],

    baseQuery: async (args, api, extraOptions) => {
        const { url, method, body, signal } = args;
        const { dispatch, getState } = api;
        try {
            const data = await authRequest(analysisBaseUrl.concat(url), dispatch, getState, {
                method: method,
                body: JSON.stringify(body),
                signal: signal,
            });

            if (typeof data === 'undefined') {
                return { data: null };
            }

            return { data };
        } catch (error) {
            return { error };
        }
    },

    endpoints: (builder) => ({
        fetchAnalysisStructure: builder.query<AnalysisStructure, FetchAnalysisStructureParams>({
            query: (args) => {
                const { fields, locale } = args;

                return {
                    method: 'GET',
                    url: `/report/sections?fields=${fields.toString()}${locale ? `&locale=${locale}` : ''}`,
                };
            },
        }),

        fetchSavedAnalysisReport: builder.query<SavedAnalysisReport, FetchSavedReportParams>({
            query: (args) => {
                const { url, guid } = args;

                return {
                    method: 'GET',
                    url: `/report/v2?url=${url}&guid=${guid}`,
                };
            },
        }),

        createAnalysisReport: builder.mutation<CreateAnalysisReportResponse, CreateAnalysisReportParams>({
            query: (args) => {
                return {
                    method: 'POST',
                    body: args,
                    url: '/report',
                };
            },
        }),

        createBackgroundAnalysisReport: builder.mutation<CreateBackgroundAnalysisResponse, CreateBackgroundAnalysisParams>({
            query: (args) => {
                return {
                    method: 'POST',
                    body: args,
                    url: '/report/background',
                };
            },
        }),

        saveReport: builder.mutation<SaveReportResponse, SaveReportParams>({
            query: (args) => {
                return {
                    method: 'POST',
                    body: args,
                    url: '/report/v2/save',
                };
            },

            // When a report is saved, invalidate this month's overviews for main url and competitors
            invalidatesTags: (result, query, args) => {
                const date = getOverviewApiEndDate(moment());

                const competitorTags = args.competitors.map((competitor) => ({ type: 'Overviews' as const, id: `${competitor.url}_${date}` }));

                return [{ type: 'Overviews', id: `${args.url}_${date}` }, ...competitorTags];
            },
        }),

        /**
         * This endpoint returns a list of overviews on success, and an empty list on any error (e.g. 404).
         * The empty list will be cached together with any other (possibly successful) fetches
         */
        fetchOverviews: builder.query<SavedAnalysisOverview[], FetchOverviewsParams>({
            // Custom queryFn for caching 404 responses
            // If overviews are not found then there are no overviews, no need to try to refetch when another component calls this endpoint
            queryFn: async (args, api, extraOptions, baseQuery) => {
                const { url, startDate, endDate } = args;

                const baseQueryArgs = {
                    method: 'GET',
                    url: `/report/v2/overviews?url=${encodeURIComponent(url)}&start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(
                        endDate
                    )}`,
                };

                const response = await baseQuery(baseQueryArgs);

                // If an error occurred cache null instead of the response list
                if (response.error) {
                    return {
                        data: [],
                    };
                } else {
                    return {
                        data: response.data,
                    };
                }
            },

            // For tag invalidation when we create a new analysis, we need to invalidate the overviews for this month
            providesTags: (result, query, args) => [{ type: 'Overviews', id: `${args.url}_${args.endDate}` }],
        }),

        fetchSectionResult: builder.query<FetchSectionResultResponse, FetchSectionResultParams>({
            query: (args) => {
                const { guid, section } = args;

                return {
                    method: 'GET',
                    url: `/report/${guid}/${section}`,
                };
            },
        }),

        fetchDomainUrls: builder.query<FetchDomainUrlsResponse, FetchDomainUrlsParams>({
            query: (args) => {
                const { domain, nextToken } = args;

                return {
                    method: 'GET',
                    url: `/report/v2/urls?domain=${domain}${nextToken ? `&next_token=${encodeURIComponent(nextToken)}` : ''}`,
                };
            },
        }),
    }),
});

export const {
    useFetchAnalysisStructureQuery,
    useFetchDomainUrlsQuery,
    useFetchOverviewsQuery,
    useLazyFetchOverviewsQuery,
    useFetchSavedAnalysisReportQuery,
    useCreateBackgroundAnalysisReportMutation,
    useSaveReportMutation,
    useCreateAnalysisReportMutation,
    useFetchSectionResultQuery,
    useLazyFetchSectionResultQuery,
    useLazyFetchDomainUrlsQuery,
} = analysisApi;
